import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  Chip,
  TextField,
  DialogActions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ThumbUp,
  Star,
  BarChart,
  TrendingDown,
  Person,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import dayjs from "dayjs";
import { OverAllLoader } from "../../components/OverAllLoader";
import { marked } from "marked";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UpdateIcon from "@mui/icons-material/Update";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useSnackbar } from "../../providers/SnackbarProvider";
import CircuitViewer from "../../components/CircuitViewer";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";
const primaryColor = "#1976d2";

// ---- Flatten the new sections[].questions[] response into ordered review cards ----
// Each card gets a question-paper-style displayNumber:
//   single  -> "1"
//   either-or            -> "11 a)" / "11 b)"
//   either-or-split      -> "11 a) i)" / "11 a) ii)" / "11 b) i)" ...
//   split (list)         -> "11 i)" / "11 ii)"
const ROMAN = ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x"];
const romanLabel = (i) => ROMAN[i] || `${i + 1}`;

function flattenSections(sections) {
  const out = [];
  (sections || []).forEach((sec, secIdx) => {
    const items = [];
    (sec.questions || []).forEach((q) => {
      const num = q.question_number;
      if (q.type === "either-or" || q.type === "either-or-split") {
        const pushOption = (opt, optLabel) => {
          if (Array.isArray(opt)) {
            // either-or-split — sub-parts i), ii), … (whole option chosen together).
            // Each sub now carries an explicit `chosen` boolean; fall back to attempt_status.
            const chosen = opt.some((s) =>
              typeof s.chosen === "boolean" ? s.chosen : (s.attempt_status ? s.attempt_status !== "UNCHOSEN" : q.chosen === `option_${optLabel}`)
            );
            opt.forEach((sub, i) =>
              items.push({ ...sub, feedback: sub.feedback ?? null, displayNumber: `${num} ${optLabel}) ${romanLabel(i)})`, eo: true, chosen, eoPair: num, eoOption: optLabel })
            );
          } else if (opt) {
            // Explicit per-option `chosen` boolean is authoritative; fall back to attempt_status/parent.
            const chosen = typeof opt.chosen === "boolean"
              ? opt.chosen
              : (opt.attempt_status ? opt.attempt_status !== "UNCHOSEN" : q.chosen === `option_${optLabel}`);
            items.push({ ...opt, feedback: opt.feedback ?? null, displayNumber: `${num} ${optLabel})`, eo: true, chosen, eoPair: num, eoOption: optLabel });
          }
        };
        pushOption(q.option_a, "a");
        pushOption(q.option_b, "b");
      } else if (q.type === "split" && Array.isArray(q.sub_parts)) {
        q.sub_parts.forEach((sub, i) =>
          items.push({ ...sub, feedback: sub.feedback ?? null, displayNumber: `${num} ${romanLabel(i)})` })
        );
      } else {
        // single / default. In a list+choice section each question carries an explicit
        // `chosen` boolean — false = the student skipped it (score 0, excluded from max).
        const hasChoice = typeof q.chosen === "boolean";
        items.push({ ...q, feedback: q.feedback ?? null, displayNumber: `${num}`, skipped: hasChoice && q.chosen === false });
      }
    });
    // Tag section info; mark the first item of the section so the render can show a PART header.
    const sectionName = sec.section_name || sec.part_name || `Part ${secIdx + 1}`;
    const choice = sec.choice;
    const choiceText =
      choice && choice.must_answer
        ? `Answer any ${choice.must_answer} of ${choice.total_given ?? sec.no_of_questions} questions`
        : null;
    items.forEach((it, i) =>
      out.push({
        ...it,
        sectionOrder: sec.section_order ?? secIdx,
        sectionHeader: i === 0 ? sectionName : null,
        sectionInstruction: i === 0 ? (choiceText || sec.instruction || sec.description || null) : null,
      })
    );
  });
  return out;
}

// The backend formats answers with 4-space indented sub-items (e.g. "    **a) DC Shunt Motor:**").
// CommonMark treats any line indented 4+ spaces as a code block, so marked.parse() renders the whole
// answer as a dark, horizontally-scrolling <pre>. Soften that leading indentation (outside fenced
// ``` blocks) so it parses as normal text / nested lists instead.
const normalizeMarkdown = (raw) => {
  if (!raw) return "";
  let inFence = false;
  return String(raw)
    .split("\n")
    .map((line) => {
      if (/^\s*```/.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      const m = line.match(/^( {4,})(\S.*)$/);
      // Keep some nesting (2 spaces) but stay below the 4-space code-block threshold.
      return m ? "  " + m[2] : line;
    })
    .join("\n");
};

const TestDetail = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  // ✅ Persist state in sessionStorage to survive refreshes
  useEffect(() => {
    if (location.state) {
      sessionStorage.setItem("current_test_detail_params", JSON.stringify(location.state));
    }
  }, [location.state]);

  const persistedParams = JSON.parse(sessionStorage.getItem("current_test_detail_params") || "{}");
  const { assessmentId, attemptId, studentId, studentName } =
    location.state || persistedParams;

  const userData = JSON.parse(localStorage.getItem("userData"));
  const userId = userData?.user_id;
  const [attempt, setAttempt] = useState({});
  const [assessmentData, setAssessmentData] = useState(null);
  const [answersData, setAnswersData] = useState([]);
  const [totalMarks, setTotalMarks] = useState(0);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [qusetionId, setQusetionId] = useState();
  const [qusetion, setQusetion] = useState();
  const [selectedIssue, setSelectedIssue] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");

  const feedbackOptions = [
    "Question is unclear",
    "Content is outdated",
    "Wrong difficulty level",
    "Other",
  ];

  const iconMap = {
    "Question is unclear": <HelpOutlineIcon fontSize="small" />,
    "Content is outdated": <UpdateIcon fontSize="small" />,
    "Wrong difficulty level": <SignalCellularAltIcon fontSize="small" />,
    Other: <MoreHorizIcon fontSize="small" />,
  };
  // ✅ Calculate total marks based on API scores
  // const totalMarks = answersData.reduce(
  //   (sum, item) => sum + Number(item.score || 0),
  //   0
  // );

  // ✅ Performance indicator
  const getPerformance = (score, maxScore = 10) => {
    const pct = maxScore ? (score / maxScore) * 100 : 0;
    if (pct >= 90) {
      return { label: "Excellent", color: "#2e7d32", icon: <Star /> };
    } else if (pct >= 70) {
      return { label: "Good", color: "#1976d2", icon: <ThumbUp /> };
    } else if (pct >= 60) {
      return { label: "Average", color: "#ed6c02", icon: <BarChart /> };
    } else {
      return { label: "Improve", color: "#d32f2f", icon: <TrendingDown /> };
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleOpen = (id, question) => {
    setOpenDialog(true);
    setQusetionId(id);
    setQusetion(question);
    setSelectedIssue("");
    setAdditionalComments("");
  };

  const handleSubmitFeedback = async () => {
    setOverAllLoading(true);
    try {
      const payload = {
        attempt_id: attempt?.attempt_id,
        question_id: qusetionId,
        feedback:
          additionalComments !== ""
            ? selectedIssue + ", " + additionalComments
            : selectedIssue,
      };
      const res = await api?.post("RefineAnswers", payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        setOpenDialog(false);
        fetchData();
      } else if (res?.data?.status === "error") {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to refine answers. Please try again.");
    } finally {
      setOverAllLoading(false);
    }
  };

  // ✅ Fetch assessment details and answers
  const fetchData = async () => {
    // Only show overall loader if we don't have cached data
    const cacheKey = `cache_test_detail_${attemptId}`;
    const hasCache = !!localStorage.getItem(cacheKey);

    if (!hasCache) setOverAllLoading(true);

    try {
      const payload = {
        user_id:
          localStorage.getItem("role") === "STUDENT" ? userId : studentId,
        assessment_id: assessmentId,
        page: 1,
        size: 1000,
        attempt_id: attemptId,
      };

      const res = await api.post("StudentAssessmentDetails", payload);
      if (res?.data?.status === "success") {
        setAssessmentData(res.data?.assessment || {});
        setAnswersData(res.data?.sections ? flattenSections(res.data.sections) : (res.data?.data || []));
        setAttempt(res.data?.attempt || {});
        setTotalMarks(res.data?.total || 0);

        // Store in local storage for instant loading on refresh
        localStorage.setItem(cacheKey, JSON.stringify(res.data));
        setOverAllLoading(false);
      }
    } catch (error) {
      console.log("Error:", error);
      setOverAllLoading(false);
    } finally {
      setOverAllLoading(false);
    }
  };

  // ✅ Load from cache on component mount
  useEffect(() => {
    if (attemptId) {
      const cachedData = localStorage.getItem(`cache_test_detail_${attemptId}`);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setAssessmentData(parsed.assessment || {});
          setAnswersData(parsed.sections ? flattenSections(parsed.sections) : (parsed.data || []));
          setAttempt(parsed.attempt || {});
          setTotalMarks(parsed.total || 0);
        } catch (e) {
          console.error("Cache parsing error:", e);
        }
      }
      fetchData();
    }
  }, [attemptId, assessmentId, userId]);


  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Fetching details..." />
      ) : (
        <Box>
          {/* Back button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 1,
              color: "#1976d2",
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Back
          </Button>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <IconButton>
                <Person sx={{ fontSize: 30, color: "#1976d2" }} />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  color: "#1976d2",
                }}
              >
                {studentName ? studentName : localStorage.getItem("UserName")}
              </Typography>
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: "bold",
                color: "#1976d2",
                mb: 2,
              }}
            >
              📝 {`${getLabel('EXAM')} Overview`}
            </Typography>
          </Grid>
          {/* Assessment Info */}
          {assessmentData && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                boxShadow: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                color: "white",
              }}
            >
              <Box>
                <Typography>📘 Subject: {assessmentData?.category}</Typography>
                <Typography>📝 Type: {assessmentData?.test_type}</Typography>
                <Typography>
                  ❓ Questions: {assessmentData?.no_of_questions}
                </Typography>
                <Typography>
                  ⏳ Time Limit: {String(assessmentData?.time_limit ?? "").replace(/\D/g, "")}{" "}
                  {Number(String(assessmentData?.time_limit).replace(/\D/g, "")) === 1 ? "minute" : "minutes"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
                  Total Marks
                </Typography>
                {(() => {
                  // Achieved score = sum of per-question scores. The total (max) marks come from
                  // the authoritative assessment.total_marks — per-question total_score is 0/null
                  // for RANDOM split & either-or sections, so summing it under-counts. Fall back
                  // to attempt.max_total, then the per-question sum for older payloads.
                  const rawTotal = answersData.reduce((s, i) => s + Number(i.score || 0), 0);
                  const maxTotal =
                    Number(assessmentData?.total_marks) ||
                    Number(attempt?.max_total) ||
                    answersData.reduce((s, i) => s + Number(i.total_score ?? i.max_score ?? 10), 0);
                  return (
                    <>
                      <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#fff", lineHeight: 1.2 }}>
                        {Number(rawTotal.toFixed(2))} / {maxTotal}
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-block",
                          mt: 0.5,
                          px: 1.25,
                          py: 0.25,
                          borderRadius: "999px",
                          backgroundColor: "rgba(255,255,255,0.25)",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: 700,
                        }}
                      >
                        {attempt?.final_score ? Math?.round(attempt?.final_score) : 0}%
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Paper>
          )}

          {/* Questions List */}
          {answersData.map((item, index) => {
            const score = Number(item.score || 0);
            const performance = getPerformance(score, item.total_score ?? item.max_score ?? 10);

            return (
              <React.Fragment key={`${item.displayNumber ?? index}-${item.question_id ?? index}`}>
                {/* PART / Section divider — keeps the overview reading like the question paper */}
                {item.sectionHeader && (
                  <Box
                    sx={{
                      mt: index === 0 ? 0 : 3,
                      mb: 1.5,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      backgroundColor: "#eef4fb",
                      borderLeft: "4px solid #1976d2",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#1976d2", letterSpacing: 0.5 }}>
                      {item.sectionHeader}
                    </Typography>
                    {item.sectionInstruction && (
                      <Typography sx={{ fontSize: "12px", color: "#5a7aa3", mt: 0.25 }}>
                        {item.sectionInstruction}
                      </Typography>
                    )}
                  </Box>
                )}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                    transition: "0.3s",
                    "&:hover": { boxShadow: 4, transform: "scale(1.01)" },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    {/* Question & Answer */}
                    <Box sx={{ width: "70%" }}>
                      <Typography
                        sx={{ fontWeight: "bold", mb: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}
                      >
                        Q{item.displayNumber ?? index + 1}:
                        {(item.eo || item.skipped) && (() => {
                          // either-or → Chosen / Not attempted on each side.
                          // list+choice single → only flag the skipped ones.
                          const isChosen = item.eo ? item.chosen : !item.skipped;
                          const label = item.eo
                            ? (isChosen ? "Chosen" : "Not attempted")
                            : "Skipped";
                          return (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.625,
                                fontSize: 11,
                                fontWeight: 600,
                                lineHeight: 1,
                                px: 1,
                                py: "4px",
                                borderRadius: "999px",
                                border: "1px solid",
                                borderColor: isChosen ? "#bbf7d0" : "#e2e8f0",
                                backgroundColor: isChosen ? "#f0fdf4" : "#f8fafc",
                                color: isChosen ? "#15803d" : "#94a3b8",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: isChosen ? "#22c55e" : "#cbd5e1",
                                }}
                              />
                              {label}
                            </Box>
                          );
                        })()}
                      </Typography>
                      <Box
                        sx={{
                          mb: 1,
                          color: "text.primary",
                          "& p": { mb: 1, lineHeight: 1.6 },
                          "& strong": { fontWeight: 700, color: "#1976d2" },
                          "& code": {
                            backgroundColor: "#f4f6f8",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontFamily: "monospace",
                            fontSize: "0.9em",
                            color: "#d32f2f",
                          },
                          "& pre": {
                            backgroundColor: "#1e1e1e",
                            color: "#d4d4d4",
                            padding: 2,
                            borderRadius: 2,
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mb: 1,
                            "& code": {
                              backgroundColor: "transparent",
                              color: "inherit",
                              padding: 0,
                            },
                          },
                          "& ul, & ol": { ml: 2, mb: 1 },
                          "& li": { mb: 0.5, lineHeight: 1.6 },
                          "& blockquote": {
                            borderLeft: "4px solid #1976d2",
                            pl: 2,
                            fontStyle: "italic",
                            color: "#666",
                            my: 1,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: marked.parse(normalizeMarkdown(item.question)),
                        }}
                      />

                      {/* Image rendering if image_url exists */}
                      {item.image_url && (
                        <Box
                          sx={{
                            mb: 3,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            p: 2,
                            backgroundColor: "#fcfcfc",
                            maxWidth: "100%",
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image_url}
                            alt="Question Diagram / Circuit"
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "350px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>
                      )}


                      {item?.feedback !== null &&
                        localStorage?.getItem("role") !== "STUDENT" && (
                          <Box
                            sx={{
                              mt: 2,
                              mb: 2,
                              p: 1.5,
                              background: "#e7f1ff",
                              borderRadius: 2,
                              border: "1px solid #c3d9ff",
                              width: "fit-content",
                              minWidth: "600px"
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: primaryColor,
                                mb: 0.5,
                              }}
                            >
                              ✓ Feedback submitted
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#245a9c" }}
                            >
                              {item?.feedback}
                            </Typography>
                          </Box>
                        )}
                      <Typography sx={{ mb: 1 }}>
                        <strong>Your Answer:</strong>{" "}
                        {item.answer ? item.answer : "Not Answered"}
                      </Typography>


                      {/* Row containing Your Diagram and Actual Diagram */}
                      {(item.student_design_answer || item.correct_answer) && (
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 3, width: "100%", mt: 2, mb: 3, flexWrap: "nowrap" }}>
                          {/* Column 1: Your Diagram */}
                          <Box sx={{ flex: 1, minWidth: "300px" }}>
                            {item.student_design_answer?.startsWith("$") ? (
                              <CircuitViewer title="Your Diagram" code={item.student_design_answer} />
                            ) : (
                              <Box sx={{ width: "100%" }}>
                                <Typography variant="caption" sx={{ fontWeight: "bold", color: primaryColor, mb: 1, display: "block" }}>
                                  Your Diagram
                                </Typography>
                                <Box sx={{ height: "350px", display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed #ccc", borderRadius: "12px", backgroundColor: "#f9f9f9" }}>
                                  <Typography variant="body2" color="text.secondary">
                                    [ No Diagram Provided ]
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>



                          {/* Column 2: Actual Diagram */}
                          <Box sx={{ flex: 1, minWidth: "300px" }}>
                            {item.correct_answer?.startsWith("$") && (
                              <CircuitViewer title="Actual Diagram" code={item.correct_answer} />
                            )}
                          </Box>
                        </Box>
                      )}




                      <Typography
                        sx={{
                          mb: 1,
                          color: "text.secondary",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        💡 Suggested Answer:
                      </Typography>

                      <Box
                        sx={{
                          ml: 3,
                          mb: 1,
                          color: "text.primary",
                          "& p": { mb: 1, lineHeight: 1.6 },
                          "& strong": { fontWeight: 700, color: "#1976d2" },
                          "& code": {
                            backgroundColor: "#f4f6f8",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontFamily: "monospace",
                            fontSize: "0.9em",
                            color: "#d32f2f",
                          },
                          "& pre": {
                            backgroundColor: "#1e1e1e",
                            color: "#d4d4d4",
                            padding: 2,
                            borderRadius: 2,
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mb: 1,
                            "& code": {
                              backgroundColor: "transparent",
                              color: "inherit",
                              padding: 0,
                            },
                          },
                          "& ul, & ol": { ml: 2, mb: 1 },
                          "& li": { mb: 0.5, lineHeight: 1.6 },
                          "& blockquote": {
                            borderLeft: "4px solid #1976d2",
                            pl: 2,
                            fontStyle: "italic",
                            color: "#666",
                            my: 1,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: marked.parse(normalizeMarkdown(item.suggested_answer)),
                        }}
                      />

                      <Typography sx={{ color: "text.secondary" }}>
                        📅 Submitted:{" "}
                        {item.updated_at
                          ? dayjs(item.updated_at).format("DD-MM-YYYY hh:mm A")
                          : item.created_at
                            ? dayjs(item.created_at).format("DD-MM-YYYY hh:mm A")
                            : "-"}
                      </Typography>
                    </Box>

                    {/* Score & Performance */}
                    <Box sx={{ width: "30%", textAlign: "right" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end", // 👉 moves both boxes to right
                          gap: 2, // space between boxes
                        }}
                      >
                        {localStorage.getItem("role") !== "STUDENT" &&
                          localStorage.getItem("role") !== "SUPER_ADMIN" && (
                            <Box
                              onClick={() =>
                                item?.feedback === null
                                  ? handleOpen(item?.question_id, item?.question)
                                  : null
                              }
                            >
                              <IconButton
                                sx={{
                                  background:
                                    item?.feedback === null
                                      ? primaryColor
                                      : "red",
                                  color: "white",
                                  cursor:
                                    item?.feedback === null
                                      ? "pointer"
                                      : "not-allowed",
                                  "&:hover": {
                                    color:
                                      item?.feedback === null
                                        ? primaryColor
                                        : "red",
                                  },
                                }}
                              >
                                <ThumbDownAltOutlinedIcon />
                              </IconButton>
                            </Box>
                          )}

                        <Box
                          sx={{
                            backgroundColor: "#1976d2",
                            px: 2,
                            py: 0.5,
                            borderRadius: "10px",
                          }}
                        >
                          <Typography sx={{ color: "white", fontWeight: "bold" }}>
                            Score: {Number(score.toFixed(2))} / {item.total_score ?? item.max_score ?? 10}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        {React.cloneElement(performance.icon, {
                          sx: { color: performance.color },
                        })}
                        <Typography
                          sx={{ fontWeight: 600, color: performance.color }}
                        >
                          {performance.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </React.Fragment>
            );
          })}
        </Box>
      )}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1,
            width: "540px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Give Feedback
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              background: "#f4f6f8",
              p: 2,
              borderRadius: 2,
              border: "1px solid #e2e5e9",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              {/* {selectedQuestionPara?.split("\n")[0]} */}
              {qusetion?.split("(a)")[0]}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1.5, fontWeight: 600 }}>
            Select an issue
          </Typography>

          {/* ISSUE CHIPS */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.2,
              mb: 3,
            }}
          >
            {feedbackOptions.map((option) => (
              <Chip
                key={option}
                icon={iconMap[option]}
                label={option}
                clickable
                onClick={() => (
                  setSelectedIssue(option), setAdditionalComments("")
                )}
                variant={selectedIssue === option ? "filled" : "outlined"}
                sx={{
                  borderRadius: "20px",
                  px: 1.5,
                  py: 0.7,
                  fontSize: "0.85rem",
                  backgroundColor:
                    selectedIssue === option ? primaryColor : "#f5f6f8",
                  color: selectedIssue === option ? "#fff" : "#374151",
                  "& .MuiChip-icon": {
                    color: selectedIssue === option ? "#fff" : primaryColor,
                  },
                  "&:hover": {
                    backgroundColor:
                      selectedIssue === option ? primaryColor : "#e7e9ec",
                  },
                }}
              />
            ))}
          </Box>

          {/* COMMENT INPUT */}
          {selectedIssue === "Other" && (
            <TextField
              placeholder="Describe more about the issue..."
              multiline
              rows={3}
              fullWidth
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              sx={{
                backgroundColor: "#f5f6f8",
                borderRadius: "10px",
                "& fieldset": { border: "none" },
                "& textarea": { fontSize: "14px" },
                mt: 1,
              }}
            />
          )}
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>

          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            disabled={
              !selectedIssue ||
              (selectedIssue === "Other" && !additionalComments.trim())
            }
            startIcon={<SendOutlinedIcon />}
            sx={{
              backgroundColor: primaryColor,
              textTransform: "none",
              px: 3,
              fontWeight: 600,
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestDetail;
